import { useCallback, useEffect, useState } from "react";
import WelcomePopupPreview from "../../components/admin/WelcomePopupPreview";
import Spinner from "../../components/ui/Spinner";
import { EmptyState, ErrorState, Help, HowItWorks, ModalShell, PersonCell, SectionCard, btnGhost, btnPrimary, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { ExistingUsersSection, emptyExistingDraft, existingDraftFrom, type ExistingDraft } from "../../components/admin/CampaignExistingUsers";
import { ApplyStatusBadge, AudienceModal } from "../../components/admin/CampaignAudienceModal";
import { RecipientsDrawer as CampaignRecipientsDrawer } from "../../components/admin/CampaignRecipientsDrawer";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { promotionsService } from "../../features/promotions/service";
import type { Campaign, CampaignPayload, PromoRole, WelcomeBonusConfig } from "../../features/promotions/types";
import { MAX_CREDITS, STATUS_BADGE, isoToIstInput, istInputToIso, parseIntStrict } from "./promotionsPageUtils";

// ── Campaigns panel ──────────────────────────────────────────────────────────
interface CampaignDraft {
  name: string;
  sellerOn: boolean;
  buyerOn: boolean;
  seller_credits: string;
  buyer_credits: string;
  starts_at: string;
  ends_at: string;
  active: boolean;
  title: string;
  message: string;
  existing: ExistingDraft;
}

function emptyCampaignDraft(): CampaignDraft {
  return { name: "", sellerOn: true, buyerOn: true, seller_credits: "", buyer_credits: "", starts_at: "", ends_at: "", active: true, title: "", message: "", existing: emptyExistingDraft() };
}

function CampaignModal({ initial, onClose, onSaved, toast }: { initial: Campaign | null; onClose: () => void; onSaved: () => void; toast: ReturnType<typeof useAdminToast> }) {
  const [d, setD] = useState<CampaignDraft>(() =>
    initial
      ? {
          name: initial.name,
          sellerOn: initial.roles.includes("seller"),
          buyerOn: initial.roles.includes("buyer"),
          seller_credits: String(initial.seller_credits),
          buyer_credits: String(initial.buyer_credits),
          starts_at: isoToIstInput(initial.starts_at),
          ends_at: isoToIstInput(initial.ends_at),
          active: initial.active,
          title: initial.title ?? "",
          message: initial.message ?? "",
          existing: existingDraftFrom(initial),
        }
      : emptyCampaignDraft(),
  );
  const [saving, setSaving] = useState(false);
  const [base, setBase] = useState<WelcomeBonusConfig | null>(null);
  useEffect(() => {
    promotionsService.getWelcomeBonus().then(setBase).catch(() => setBase(null));
  }, []);
  const patch = (p: Partial<CampaignDraft>) => setD((prev) => ({ ...prev, ...p }));
  const locked = initial?.apply_status === "running";

  const submit = async () => {
    const name = d.name.trim();
    if (!name) return toast.showError("Campaign name is required");
    const roles: PromoRole[] = [];
    if (d.sellerOn) roles.push("seller");
    if (d.buyerOn) roles.push("buyer");
    if (!roles.length) return toast.showError("Select at least one role");
    // An unticked role grants nothing: its (disabled) input is neither required nor parsed.
    let s = 0;
    let b = 0;
    if (d.sellerOn) {
      const n = parseIntStrict(d.seller_credits);
      if (n === null || n > MAX_CREDITS) return toast.showError(`Seller credits must be a whole number 0-${MAX_CREDITS}`);
      if (n < 1) return toast.showError("Seller credits must be at least 1 when sellers are included");
      s = n;
    }
    if (d.buyerOn) {
      const n = parseIntStrict(d.buyer_credits);
      if (n === null || n > MAX_CREDITS) return toast.showError(`Buyer credits must be a whole number 0-${MAX_CREDITS}`);
      if (n < 1) return toast.showError("Buyer credits must be at least 1 when buyers are included");
      b = n;
    }
    if (!d.starts_at || !d.ends_at) return toast.showError("Start and end date/time (IST) are required");
    const startsIso = istInputToIso(d.starts_at);
    const endsIso = istInputToIso(d.ends_at);
    if (new Date(endsIso).getTime() <= new Date(startsIso).getTime()) return toast.showError("End must be after start");
    const ex = d.existing;
    const audienceRoles = ex.audience_roles.filter((r) => roles.includes(r));
    if (ex.include_existing) {
      if (!audienceRoles.length && !ex.audience_companies.length && !ex.extra_users.length) {
        return toast.showError("Pick who gets it: a role, a company or specific users. Nothing picked means nobody.");
      }
      const hasCredits = roles.some((r) => (r === "seller" ? s : b) > 0);
      if (!hasCredits) return toast.showError("Include existing users needs credits above 0 for at least one role");
    }
    const body: CampaignPayload = {
      name,
      roles,
      seller_credits: s,
      buyer_credits: b,
      starts_at: startsIso,
      ends_at: endsIso,
      active: d.active,
      title: d.title.trim() || null,
      message: d.message.trim() || null,
      include_existing: ex.include_existing,
      grant_mode: ex.grant_mode,
      audience_roles: audienceRoles,
      audience_companies: ex.audience_companies,
      extra_user_ids: ex.extra_users.map((u) => u.id),
      notify: ex.notify,
      notify_title: ex.notify_title.trim() || null,
      notify_message: ex.notify_message.trim() || null,
    };
    setSaving(true);
    try {
      if (initial) await promotionsService.updateCampaign(initial.id, body);
      else await promotionsService.createCampaign(body);
      toast.showSuccess(initial ? "Campaign updated" : "Campaign created");
      onSaved();
    } catch (e) {
      toast.showError(errMsg(e, "Failed to save campaign"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={initial ? "Edit campaign" : "New festive campaign"} onClose={onClose} wide>
      <div className="space-y-4">
        <HowItWorks>
          <p>Applies to users who FINISH onboarding inside this window; it is not retroactive.</p>
          <p>If several campaigns are live for a role, the highest amount wins; they do not stack. With no live campaign, the base welcome bonus applies.</p>
        </HowItWorks>
        <div>
          <label className={labelCls}>Name</label>
          <input className={inputCls} value={d.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Diwali 2026" maxLength={120} />
          <Help>Internal label, also shown as a pill on the user's welcome popup.</Help>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-300 mb-1">
              <input type="checkbox" disabled={locked} checked={d.sellerOn} onChange={(e) => patch({ sellerOn: e.target.checked })} /> Sellers
            </label>
            <input type="number" min={0} max={MAX_CREDITS} disabled={locked || !d.sellerOn} className={`${inputCls} disabled:opacity-50`} value={d.seller_credits} onChange={(e) => patch({ seller_credits: e.target.value })} placeholder="Credits" />
            <Help>Credits granted instead of the base welcome bonus. 0 = not for this role (untick the box to exclude sellers).{locked ? " Locked while an Apply is running." : ""}</Help>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-gray-300 mb-1">
              <input type="checkbox" disabled={locked} checked={d.buyerOn} onChange={(e) => patch({ buyerOn: e.target.checked })} /> Buyers
            </label>
            <input type="number" min={0} max={MAX_CREDITS} disabled={locked || !d.buyerOn} className={`${inputCls} disabled:opacity-50`} value={d.buyer_credits} onChange={(e) => patch({ buyer_credits: e.target.value })} placeholder="Credits" />
            <Help>Credits granted instead of the base welcome bonus. 0 = not for this role (untick the box to exclude buyers).{locked ? " Locked while an Apply is running." : ""}</Help>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Starts (IST)</label>
            <input type="datetime-local" className={inputCls} value={d.starts_at} onChange={(e) => patch({ starts_at: e.target.value })} />
            <Help>Required. Campaign begins at this time (IST).</Help>
          </div>
          <div>
            <label className={labelCls}>Ends (IST)</label>
            <input type="datetime-local" className={inputCls} value={d.ends_at} onChange={(e) => patch({ ends_at: e.target.value })} />
            <Help>Required. Must be after the start (IST). Users finishing onboarding later get the base bonus.</Help>
          </div>
        </div>
        <div>
          <label className={labelCls}>Popup title (optional)</label>
          <input className={inputCls} value={d.title} onChange={(e) => patch({ title: e.target.value })} maxLength={120} placeholder="Happy Diwali from Gullivr!" />
          <Help>Popup heading during this campaign. Blank = the Welcome Bonus title / default text.</Help>
        </div>
        <div>
          <label className={labelCls}>Popup message (optional — {"{credits}"} is replaced)</label>
          <textarea className={inputCls} rows={3} value={d.message} onChange={(e) => patch({ message: e.target.value })} maxLength={500} placeholder="Celebrate with {credits} free credits — on us." />
          <Help>Popup body. {"{credits}"} is replaced by the amount the user received. Blank = the Welcome Bonus message / default text.</Help>
        </div>
        <WelcomePopupPreview
          sellerCredits={d.seller_credits}
          buyerCredits={d.buyer_credits}
          title={d.title.trim() || base?.title || ""}
          message={d.message.trim() || base?.message || ""}
          campaignName={d.name.trim() || null}
        />
        <ExistingUsersSection
          value={d.existing}
          onChange={(p) => setD((prev) => ({ ...prev, existing: { ...prev.existing, ...p } }))}
          enabledRoles={[...(d.sellerOn ? ["seller" as const] : []), ...(d.buyerOn ? ["buyer" as const] : [])]}
          sellerCredits={d.seller_credits}
          buyerCredits={d.buyer_credits}
          campaignName={d.name}
          toast={toast}
          locked={locked}
        />
        <div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={d.active} onChange={(e) => patch({ active: e.target.checked })} /> Active
          </label>
          <Help>Untick to pause the campaign instantly without deleting it.</Help>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} className={btnPrimary}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </ModalShell>
  );
}

export default function CampaignsPanel({ toast, reloadKey, onSummary }: { toast: ReturnType<typeof useAdminToast>; reloadKey: number; onSummary: (s: string) => void }) {
  const [rows, setRows] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ open: boolean; item: Campaign | null }>({ open: false, item: null });
  const [audience, setAudience] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<Campaign | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await promotionsService.listCampaigns();
      setRows(list);
      setLoadError(null);
      const live = list.filter((c) => c.status === "live").length;
      onSummary(`${list.length} total, ${live} live`);
    } catch (e) {
      setLoadError(errMsg(e, "Failed to load campaigns"));
    } finally {
      setLoading(false);
    }
  }, [onSummary]);

  useEffect(() => { void load(); }, [load, reloadKey]);

  const remove = async (c: Campaign) => {
    if (!window.confirm(`Delete campaign "${c.name}"? This cannot be undone.`)) return;
    setDeleteError(null);
    try {
      await promotionsService.deleteCampaign(c.id);
      toast.showSuccess("Campaign deleted");
      await load();
    } catch (e) {
      // 409: an apply run is in progress, or the campaign already has recipients.
      const msg = errMsg(e, "Failed to delete campaign");
      setDeleteError(`"${c.name}" was not deleted. ${msg}`);
      toast.showError(msg);
    }
  };

  return (
    <SectionCard
      id="festive-campaigns"
      title="Festive campaigns"
      description="Scheduled windows that override the welcome bonus per role. Highest live amount wins; times are IST. Changes apply immediately to the next user who finishes onboarding."
      action={<button onClick={() => setModal({ open: true, item: null })} className={`${btnPrimary} shrink-0`}>New campaign</button>}
    >
      {deleteError && (
        <div role="alert" className="mb-3 flex items-start justify-between gap-3 text-sm bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg px-3 py-2">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-amber-300 hover:text-white leading-none" aria-label="Dismiss">×</button>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={() => { setLoading(true); void load(); }} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No campaigns yet"
          hint="Create a festive campaign to pay a different amount than the welcome bonus for a limited time, and optionally reward existing users too."
          action={<button onClick={() => setModal({ open: true, item: null })} className={btnPrimary}>New campaign</button>}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-3 font-medium">Name</th>
                <th className="py-2 pr-3 font-medium">Roles / credits</th>
                <th className="py-2 pr-3 font-medium">Window (IST)</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Last change</th>
                <th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-gray-800/60 text-gray-300">
                  <td className="py-2.5 pr-3 text-white">
                    {c.name}
                    {c.include_existing && (
                      <div className="text-xs text-gray-500 font-normal">
                        Existing users, {c.grant_mode === "claim" ? "user claims" : "admin applies"}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 whitespace-nowrap">
                    {c.roles.includes("seller") && <div>Seller: {c.seller_credits}</div>}
                    {c.roles.includes("buyer") && <div>Buyer: {c.buyer_credits}</div>}
                  </td>
                  <td className="py-2.5 pr-3 text-xs whitespace-nowrap">
                    <div>{formatISTDateTime(c.starts_at)}</div>
                    <div className="text-gray-500">to {formatISTDateTime(c.ends_at)}</div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[c.status] ?? STATUS_BADGE.inactive}`}>{c.status}</span>
                    {c.include_existing && <div className="mt-1"><ApplyStatusBadge status={c.apply_status} error={c.apply_error} /></div>}
                  </td>
                  <td className="py-2.5 pr-3 text-xs min-w-[8rem]">
                    {c.updated_at && <div className="text-gray-400 whitespace-nowrap">{formatISTDateTime(c.updated_at)}</div>}
                    {c.updated_by_user ? <PersonCell person={c.updated_by_user} fallback="an admin" /> : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="py-2.5 text-right">
                    {c.include_existing && (
                      <>
                        <button onClick={() => setAudience(c)} className="text-xs px-2 py-1 rounded-md text-emerald-400 hover:bg-emerald-500/10">
                          Preview audience
                        </button>
                        <button onClick={() => setRecipients(c)} className="text-xs px-2 py-1 rounded-md text-gray-300 hover:bg-gray-800">Recipients</button>
                      </>
                    )}
                    <button onClick={() => setModal({ open: true, item: c })} className="text-xs px-2 py-1 rounded-md text-indigo-400 hover:bg-indigo-500/10">Edit</button>
                    <button
                      onClick={() => remove(c)}
                      disabled={c.apply_status === "running"}
                      title={c.apply_status === "running" ? "An apply run is in progress; wait for it to finish." : "Campaigns that already have recipients cannot be deleted; deactivate them instead."}
                      className="text-xs px-2 py-1 rounded-md text-red-400 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal.open && (
        <CampaignModal
          initial={modal.item}
          toast={toast}
          onClose={() => setModal({ open: false, item: null })}
          onSaved={() => { setModal({ open: false, item: null }); void load(); }}
        />
      )}
      {audience && <AudienceModal campaign={audience} toast={toast} onClose={() => setAudience(null)} onChanged={() => void load()} />}
      {recipients && <CampaignRecipientsDrawer campaign={recipients} toast={toast} onClose={() => setRecipients(null)} />}
    </SectionCard>
  );
}
